import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  type TimeRange,
  type TimeRangePreset,
} from "@/lib/timeRange";
import { SEVERITY_VALUES, type RuleMetadataResponse, type Severity } from "@/api";

export interface DetectionFilters {
  severities: Severity[];
  ruleId: string | null;
  timeRange: TimeRange;
}

export const DEFAULT_FILTERS = (timeRange: TimeRange): DetectionFilters => ({
  severities: [],
  ruleId: null,
  timeRange,
});

const PRESETS: TimeRangePreset[] = ["1h", "24h", "7d"];
const PRESET_LABEL: Record<TimeRangePreset, string> = {
  "1h": "Last hour",
  "24h": "Last 24h",
  "7d": "Last 7d",
};

export function FilterBar({
  filters,
  rules,
  onChange,
}: {
  filters: DetectionFilters;
  rules: RuleMetadataResponse[];
  onChange: (next: DetectionFilters) => void;
}): JSX.Element {
  const toggleSeverity = (severity: Severity) => {
    const next = filters.severities.includes(severity)
      ? filters.severities.filter((s) => s !== severity)
      : [...filters.severities, severity];
    onChange({ ...filters, severities: next });
  };

  const setRule = (ruleId: string) => {
    onChange({ ...filters, ruleId: ruleId === "" ? null : ruleId });
  };

  const setPreset = (preset: TimeRangePreset) => {
    onChange({ ...filters, timeRange: { kind: "preset", preset } });
  };

  const setCustom = () => {
    // Initialise the custom window from the current preset's resolved
    // bounds so toggling Custom doesn't snap to an empty range.
    const now = new Date();
    const since = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    onChange({
      ...filters,
      timeRange: { kind: "custom", since, until: now },
    });
  };

  const updateCustom = (which: "since" | "until", value: string) => {
    if (filters.timeRange.kind !== "custom") return;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return;
    onChange({
      ...filters,
      timeRange: { ...filters.timeRange, [which]: date },
    });
  };

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">
          Severity
        </span>
        {SEVERITY_VALUES.map((severity) => {
          const active = filters.severities.includes(severity);
          return (
            <button
              key={severity}
              type="button"
              onClick={() => toggleSeverity(severity)}
              className={cn(
                "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors",
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background text-muted-foreground hover:bg-accent",
              )}
            >
              {severity}
            </button>
          );
        })}
        {filters.severities.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange({ ...filters, severities: [] })}
          >
            Clear
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor="rule-select"
          className="text-sm font-medium text-muted-foreground"
        >
          Rule
        </label>
        <select
          id="rule-select"
          value={filters.ruleId ?? ""}
          onChange={(event) => setRule(event.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All rules</option>
          {rules.map((rule) => (
            <option key={rule.rule_id} value={rule.rule_id}>
              {rule.display_name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">
          Time range
        </span>
        {PRESETS.map((preset) => (
          <Button
            key={preset}
            type="button"
            variant={
              filters.timeRange.kind === "preset" &&
              filters.timeRange.preset === preset
                ? "default"
                : "outline"
            }
            size="sm"
            onClick={() => setPreset(preset)}
          >
            {PRESET_LABEL[preset]}
          </Button>
        ))}
        <Button
          type="button"
          variant={filters.timeRange.kind === "custom" ? "default" : "outline"}
          size="sm"
          onClick={setCustom}
        >
          Custom
        </Button>
        {filters.timeRange.kind === "custom" && (
          <div className="flex items-center gap-2">
            <Input
              type="datetime-local"
              value={toLocalDatetimeInputValue(filters.timeRange.since)}
              onChange={(event) => updateCustom("since", event.target.value)}
              className="h-9 w-auto"
            />
            <span className="text-sm text-muted-foreground">→</span>
            <Input
              type="datetime-local"
              value={toLocalDatetimeInputValue(filters.timeRange.until)}
              onChange={(event) => updateCustom("until", event.target.value)}
              className="h-9 w-auto"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** datetime-local inputs use local-time strings without timezone suffix. */
function toLocalDatetimeInputValue(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}
